/**
 * Removes the consumer's added quote from their plan, after a confirmation. When an application was
 * already sent for it, the confirmation says plainly that removing it won't cancel or withdraw the
 * application. Shown as a "Remove policy" link, or as a bin icon on Your Plan's policy cards.
 */
import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Button,
} from '@design-system';
import { useAddedQuote } from '../hooks/use-added-quote';

interface RemoveAddedQuoteButtonProps {
  clientId: string;
  hasApplication: boolean;
  onRemoved?: () => void;
  appearance?: 'link' | 'icon';
}

export function RemoveAddedQuoteButton({
  clientId,
  hasApplication,
  onRemoved,
  appearance = 'link',
}: RemoveAddedQuoteButtonProps) {
  const { removeAddedQuote, isRemoving, removeFailed } = useAddedQuote(clientId);
  const [isOpen, setIsOpen] = useState(false);

  const remove = async () => {
    try {
      await removeAddedQuote();
      setIsOpen(false);
      onRemoved?.();
    } catch {
      // `removeFailed` shows the error in the dialog.
    }
  };

  return (
    <AlertDialog
      open={isOpen}
      onOpenChange={setIsOpen}
    >
      <AlertDialogTrigger asChild>
        {appearance === 'icon' ? (
          <Button
            variant="ghost"
            size="icon-lg"
            className="text-muted-foreground -my-2.5 -mr-2.5"
            aria-label="Remove policy"
            disabled={isRemoving}
          >
            <Trash2 />
          </Button>
        ) : (
          <Button
            variant="link"
            className="self-start px-0 has-[>svg]:px-0"
            disabled={isRemoving}
          >
            Remove policy
          </Button>
        )}
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Remove this policy from your plan?</AlertDialogTitle>
          <AlertDialogDescription>
            It will stop counting toward your coverage. You can choose it again later.
            {hasApplication && (
              <>
                {' '}
                Your application has already been sent. Removing this policy from your plan{' '}
                <strong>won’t cancel or withdraw your application</strong>.
              </>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {removeFailed && (
          <p className="text-destructive text-sm">
            We couldn’t remove this policy. Please try again.
          </p>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel>Keep it</AlertDialogCancel>
          <AlertDialogAction
            onClick={event => {
              event.preventDefault();
              void remove();
            }}
            disabled={isRemoving}
          >
            {isRemoving ? 'Removing…' : 'Remove policy'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
